import uniq from "lodash/uniq";
import moment from "moment";

import Analytics from "analytics/util/Analytics";
import getImagePath from "shared/util/getImagePath";
import makeSmartDateString from "shared/util/makeSmartDateString";

import {
	WINDOW_STATE,
	STANDALONE_PAGE_URL,
	SEND_TRIGGER_TYPE,
} from "apps/messenger/util/consts";
import ProfileComment from "shared/components/ProfileComment";
import GetUserIdFromWindowLocation from "shared/util/GetUserIdFromWindowLocation";
import GetUrlFromPicid from "shared/util/GetUrlFromPicid";
import { isMobile } from "shared/util/EnvironmentHelpers";
import { LikedVia } from "apps/messenger/components/Connections/constants";
import OkPhotos from "shared/modules/OkPhotos";
import _str from "shared/util/_str";
import OkModal from "reflux/okmodal/app";
import OkModalManager from "reflux/okmodal/util/OkModalManager";

export const isStandalonePage = window.location.pathname === STANDALONE_PAGE_URL;

export const getStandalonePageUrl = (userid) => {
	return `${window.location.origin}${STANDALONE_PAGE_URL}?userid=${userid}`;
};

export const openStandalonePage = (userid) => {
	const newWindowUrl = getStandalonePageUrl(userid);
	const newWindowTitle = _str(`OkCupid Conversation - ${userid}`);
	const newWindowOptions = {
		width: 400,
		height: 640,
		menubar: "no",
		toolbar: "no",
		location: "no",
		personalbar: "no",
		scrollbars: "no",
		status: "no",
		resizable: "yes",
	};
	const newWindowOptionsSerialized = Object.keys(newWindowOptions)
		.map((key) => `${key}=${newWindowOptions[key]}`)
		.join(",");

	window.open(
		newWindowUrl,
		newWindowTitle,
		newWindowOptionsSerialized,
	);
};

export const closeStandalonePage = () => {
	window.close();
};

// Checks if a conversation is active, based on where we're viewing this convo.
export const isConversationActive = ({ userid, state } = {}) => {
	if (!userid) {
		return false;
	}

	const urlUserId = GetUserIdFromWindowLocation();
	return ((isMobile || isStandalonePage) && userid === urlUserId) || state === WINDOW_STATE.Open;
};

// Redux Helpers
// --------------------------------------

// Gets the current timestamp.
export function generateTimestamp() {
	return Date.now();
}

// Generates a temporary messageId—useful for keeping
// tabs on a message in redux while we send it.
export function generateTemporaryMessageId() {
	return `${Math.round(Math.random() * 10000)}`;
}

// Sort an unsorted set of messages.
export function sortMessages(messagesUnsorted) {
	return [...messagesUnsorted].sort((a, b) => (a.timestamp - b.timestamp));
}

// Get the last message from a user in a list of messages.
export function getLastMessageFromUser(userid, messages) {
	return sortMessages([...messages])
		.reverse()
		.find(({ from }) => from === userid);
}

// Abstracts away page visible/hidden logic.
// It'll just call the appropriate functions as necessary.
export function setupPageVisibilityListeners({
	onPageVisible,
	onPageHidden,
}) {
	// Set the name of the change event for visibility (Browsers suck.)
	let visibilityChange;

	if (typeof document.hidden !== "undefined") {
		visibilityChange = "visibilitychange";
	} else if (typeof document.msHidden !== "undefined") {
		visibilityChange = "msvisibilitychange";
	} else if (typeof document.webkitHidden !== "undefined") {
		visibilityChange = "webkitvisibilitychange";
	}

	// If browser supports Page Visibility API, use that.
	// Unless it's a standalone page, then use focus events.
	if (!isStandalonePage && visibilityChange) {
		const onVisibilityChange = () => {
			// The name of the document hidden event (Browsers *suck*.)
			const hidden = {
				"visibilitychange": "hidden",
				"msvisibilitychange": "msHidden",
				"webkitvisibilitychange": "webkitHidden",
			}[visibilityChange];

			if (document[hidden]) {
				onPageHidden();
			} else {
				onPageVisible();
			}
		};

		document.addEventListener(visibilityChange, onVisibilityChange);

		// Call this once to initialize.
		onVisibilityChange();
	}

	// Fallback to blur/focus events.
	else {
		// Window has left focus.
		window.addEventListener("blur", onPageHidden);

		// Window has been focused again.
		window.addEventListener("focus", onPageVisible);

		// Call this once to initialize.
		onPageVisible();
	}
}

// Takes a userid, its conversation data, and optionally state,
// And normalizes it for our redux store.
export function normalizeConversationData(userid, conversation = {}, isInOrder) {
	// If we don't have this conversation, exit.
	if (!conversation) {
		return null;
	}

	// Defaults.
	const likedThisSession = false;
	const showScammerWarning = conversation.showScammerWarning || false;
	const showContentWarning = conversation.showContentWarning || false;
	const state = isInOrder ? conversation.state || WINDOW_STATE.Overflow : WINDOW_STATE.Overflow;
	const draft = conversation.draft || null;

	// Move this into a camel-cased property.
	if (draft && draft.profile_comment) {
		draft.profileComment = { ...draft.profile_comment };

		delete draft.profile_comment;
	}

	const loadingMessages = false;
	const totalMessages = conversation.totalMessages || 0;

	// Mark the last read message as read.
	let reachedLastRead = false;
	let unreadCount = conversation.unreadCount || 0;
	const messages = sortMessages(conversation.messages || [])
		.map((message) => {
			const body = message.body || "";

			// Move these into camel-cased properties.
			const profileComment = message.profile_comment && {
				...message.profile_comment,
			};
			const profileLinkRequest = message.profile_link_request && {
				...message.profile_link_request,
			};

			if (message.id === conversation.properties.last_read) {
				reachedLastRead = true;

				return {
					...message,
					body,
					profileComment,
					profileLinkRequest,
					markedRead: true,
				};
			}

			// Increment the unread count.
			if (reachedLastRead && message.from === userid) {
				unreadCount += 1;
			}

			return {
				...message,
				body,
				profileComment,
				profileLinkRequest,
			};
		});

	// Persist the normalized state.
	return {
		userid,
		...conversation,
		likedThisSession,
		showScammerWarning,
		showContentWarning,
		state,
		draft,
		loadingMessages,
		totalMessages,
		unreadCount,
		messages,
	};
}

// Normalize the backend state for the redux store.
export function normalizeMessengerState(data) {
	const needsPhoneVerification = data.needsPhoneVerification;
	let conversationOrder = data.conversationOrder;
	const conversationData = {};

	// Remove users from conversation order who I have not yet matched with.
	// Unless I'm on their profile page.
	conversationOrder = uniq(conversationOrder.filter((userid) => {
		const conversation = data.conversationData[userid];
		const isMutual = conversation && conversation.fields.likes.mutual_like;
		const isOnProfile = GetUserIdFromWindowLocation() === userid ||
			GetUserIdFromWindowLocation() === conversation.fields.username;

		// Only persist conversations that are from a mutual like.
		return isMutual || isOnProfile;
	}));

	// Keep track of whether we have a non-overflowed convo.
	let hasMainConvo = false;

	// Setup conversation defaults.
	Object.keys(data.conversationData).forEach((userid) => {
		const isInOrder = conversationOrder.indexOf(userid) >= 0;
		conversationData[userid] = normalizeConversationData(
			userid,
			data.conversationData[userid],
			isInOrder,
		);

		// Check if it's not overflowed.
		if (conversationData[userid] && conversationData[userid].state !== WINDOW_STATE.Overflow) {
			hasMainConvo = true;
		}
	});

	// All of the convos are overflowed!
	// Set the first one to be minimized.
	if (!hasMainConvo && conversationOrder.length) {
		conversationData[conversationOrder[0]].state = WINDOW_STATE.Minimized;
	}

	const preferredSendTrigger = data.enterToSend
		? SEND_TRIGGER_TYPE.EnterToSend
		: SEND_TRIGGER_TYPE.ClickToSend;

	return {
		conversationData,
		conversationOrder,
		preferredSendTrigger,
		needsPhoneVerification,
		messagePledge: data.messagePledge,
	};
}

// Denormalize the store state for the backend.
export function denormalizeMessengerState(state) {
	const conversationOrder = state.conversationOrder;
	const conversationStates = conversationOrder.reduce((states, userid) => {
		const conversation = state.conversationData[userid];

		return {
			...states,
			[userid]: conversation.state,
		};
	}, {});
	const enterToSend = state.preferredSendTrigger;
	const allowOverflowNotification = false;

	return {
		order: conversationOrder,
		states: conversationStates,
		enter_to_send: enterToSend,
		allow_overflow_notification: allowOverflowNotification,
	};
}

// UI Helpers
// --------------------------------------

// Extracts the appropriate set of user instructions
// to show for a given conversation.
export function getUserInstruction({
	userInstructions,
	profileComment,
	likedThisSession,
	showOcsBlock,
}) {
	const noCommentImage = `${getImagePath()}media/img/illustrations/wright-png/admirer-bg@2x.png`;
	const messageSentImage = `${getImagePath()}media/img/gifs/sent-message-once-transparent.gif`;
	const commentType = profileComment && profileComment.comment_type;

	// Get an instructional tip to show.
	let userInstruction = {
		image: null,
		title: "",
		body: "",
	};

	const isEssayComment = commentType === ProfileComment.type.Essay;
	const isPhotoComment = [
		ProfileComment.type.Photo,
		ProfileComment.type.InstagramPhoto,
	].indexOf(commentType) >= 0;

	if (userInstructions) {
		if (showOcsBlock) {
			userInstruction = {
				...(userInstructions.ocsMessagingSentMessage || {
					title: _str("You sent them a message!"),
					body: _str("If it’s meant to be, you’ll hear back. Until then, you won’t be able to view their profile or your sent message."),
				}),
				image: messageSentImage,
				linkText: isMobile && _str("Keep Browsing"),
				link: isMobile && "/doubletake",
			};
		} else {
			// Did they "like" or "comment"?
			const likeOrMessage = likedThisSession ? "Like" : "Message";

			// What did they like/comment?
			let content = "Profile";
			if (isEssayComment) content = "Essay"; // eslint-disable-line block-function-spacing/space-around-blocks
			if (isPhotoComment) content = "Photo"; // eslint-disable-line block-function-spacing/space-around-blocks

			// Build up the key to get the right message.
			const key = `ocsMessaging${likeOrMessage}${content}`;

			userInstruction = {
				...(userInstructions[key] || {}),
				image: content === "Profile" && noCommentImage,
			};
		}
	}

	// Remove any line breaks.
	if (userInstruction && userInstruction.body) {
		userInstruction.body = userInstruction.body.replace("<br/>", " ");
	}

	return userInstruction;
}

// Scroll to the last message in the list.
export function scrollToLastMessage(msgContainer, options) {
	// Exit if we don't have children.
	if (!msgContainer || !msgContainer.children || !msgContainer.children.length) {
		return;
	}

	const containerRightBound = msgContainer.getBoundingClientRect().right;
	const windowRightBound = document.documentElement.clientWidth;
	const isOffscreen = containerRightBound > windowRightBound;

	// If the container is offscreen, just snap to the bottom.
	if (isOffscreen) {
		// eslint-disable-next-line no-param-reassign
		msgContainer.scrollTop = msgContainer.scrollHeight - msgContainer.clientHeight;

		return;
	}

	// Get the last child element.
	const lastMsg = msgContainer.children[msgContainer.children.length - 1];
	lastMsg.scrollIntoView(options);
}

// Calculates N-ways in a list of messages.
export function getNways(messagesUnsorted) {
	const messages = sortMessages(messagesUnsorted);

	let last;

	return messages.reduce((nway, message) => {
		// skip mutual matches
		if (message.is_mutual_match) {
			return getNways;
		}

		// if the sender is different from the last, increment and make last
		if (last !== message.from) {
			last = message.from;
			return nway + 1;
		}

		return nway;
	}, 0);
}

export function timestampToDate(timestamp) {
	return new Date(timestamp * 1000);
}

// Determine if we should show a timestamp based on time since last timestamp.
// Rules are: show if on different day, or > 15m since last timestamp.
export function shouldShowTimestamp(messageTimestamp, prevMessageTimestamp) {
	const prevMessageDate = timestampToDate(prevMessageTimestamp);
	const messageDate = timestampToDate(messageTimestamp);

	let showTimestamp = false;

	// If message more than a day since last stamp.
	if (prevMessageDate.toDateString() !== messageDate.toDateString()) {
		showTimestamp = true;
	}

	// If message was sent at least 15m since last stamp.
	else if ((messageTimestamp - prevMessageTimestamp) > (15 * 60)) {
		showTimestamp = true;
	}

	return showTimestamp;
}

// Given a list of messages, returns an array of ids of messages
// that should show their timestamp, according to the rules as
// defined in shouldShowTimestamp.
export function findMessagesToShowTimestampsFor(messages) {
	// Sort by timestamp first.
	const allMessages = sortMessages(messages);

	// Reduce down to array of messages that need a timestamp.
	const messagesWithTimestamps = allMessages.reduce((messagesSoFar, currentMessage) => {
		// Find most recent message with a timestamp.
		const lastMessageWithTimestamp = messagesSoFar[messagesSoFar.length - 1];

		// No previous message, always show a timestamp.
		if (!lastMessageWithTimestamp) {
			return [currentMessage];
		}

		// Check if we should show a timestamp for this message.
		else if (shouldShowTimestamp(currentMessage.timestamp, lastMessageWithTimestamp.timestamp)) {
			return [...messagesSoFar, currentMessage];
		}

		// Keep searching.
		else {
			return messagesSoFar;
		}
	}, []);

	// Just return the ids.
	return messagesWithTimestamps.map(({ id }) => id);
}

// Given a list of messages, returns id of last read message.
export function findMessageToShowReadIndicatorFor(messages) {
	// Sort by timestamp first.
	const allMessages = sortMessages(messages);
	const lastReadMessage = allMessages.reverse().find(({ read }) => read);

	return lastReadMessage ? lastReadMessage.id : null;
}

export function findMessageToShowUnreadIndicatorFor(messages, currentUserId) {
	// given a list of messagees, returns the id of the last unread message
	const messagesSortedByTimestamp = sortMessages(messages).reverse();
	const lastUnreadMessage = messagesSortedByTimestamp.find((message) => {
		return !message.read && message.from === currentUserId;
	});

	const lastReadMessage = messagesSortedByTimestamp.find(({ read }) => read);

	if ((lastReadMessage || {}).timestamp > (lastUnreadMessage || {}).timestamp) {
		return null;
	}

	return lastUnreadMessage ? lastUnreadMessage.id : null;
}

// Uses makeSmartDateString to make a pretty looking date.
export function makePrettyDate(date) {
	return makeSmartDateString(
		date,
		"BRIEF_MAIL_WITH_TIME",
	).replace(/<\/*em>/g, "");
}

// Check if user has explicitly granted permissions.
export function canNotify() {
	if (!("Notification" in window)) {
		return false;
	}

	return Notification.permission === "granted";
}

// Request push notification permission if necessary.
// Resolves promise with bool indicating permission granted.
export function requestNotificationPermission() {
	// No push support.
	if (!("Notification" in window)) {
		return Promise.resolve(false);
	}

	// We've already denied permission, exit.
	if (Notification.permission === "denied") {
		return Promise.resolve(false);
	}

	// We've already granted permission, exit.
	if (Notification.permission === "granted") {
		return Promise.resolve(true);
	}

	// Prompt the user.
	return Notification.requestPermission((permission) => {
		const isGranted = permission === "granted";

		Analytics.logUIEvent(Analytics.eventNames.allowedBrowserNotification, {
			data: {
				"browser notification": isGranted
					? "selected yes"
					: "selected no",
			},
		});

		return Promise.resolve(isGranted);
	});
}

// Displays a push notification (after asking for permission, of course).
export function notify(title = "", body = "", options = {}) {
	// Check if we have permission.
	if (!canNotify()) {
		return;
	}

	// Create the notification.
	const notification = new Notification(title, {
		body,
		icon: `${getImagePath()}media/img/dtf/join_button_logo@2x.png`,
		...options,
	});

	// Focus the window when they click the notification.
	notification.addEventListener("click", () => window.focus());

	// Auto-close it 4s later (most browsers do this automatically).
	setTimeout(notification.close.bind(notification), 4000);
}

// Converts draft held in conversationData state to props being consumed by ConnectionLayer
export function mapDraftToConnectionLayer({
	draft,
}) {
	const draftComment = (draft && draft.profileComment) || {};
	const commentContent = draftComment.comment_content || {};

	const likedPhoto = commentContent.photos
		? commentContent.photos.large
		: GetUrlFromPicid(commentContent.picid);

	const likedEssay = typeof commentContent.essay_id !== "undefined" && commentContent.essay_id !== null ? {
		clean_content: commentContent.essay_content,
		title: commentContent.essay_title,
	} : null;

	let likedVia = LikedVia.GLOBAL;

	if (draftComment && draftComment.comment_type === "essay") {
		likedVia = LikedVia.ESSAY;
	} else if (draftComment && draftComment.comment_type === "photo") {
		likedVia = LikedVia.PHOTO;
	}

	return {
		likedPhoto,
		likedEssay,
		likedVia,
	};
}

export function shouldShowConnectionLayer({
	likedThisSession,
	targetUser = {},
	hasSentAMessage,
	canMessage,
	platform,
	nway,
}) {
	if (platform === "mobile") return false;

	// Checks for inbox capacity, feedback agent status, inactivity of account
	// and if the target user is OkCupid (for like linking and stuff)
	if (!canMessage) return false;

	if (targetUser.blocked) return false;

	if (nway >= 2) return false;

	const isMutualMatch = !!(targetUser.likes && targetUser.likes.they_like);

	if (isMutualMatch && likedThisSession && !hasSentAMessage) return true;

	if (!isMutualMatch) return true;

	return false;
}

export function showPhotoBlock() {
	new OkModal({
		className: "OkModal-photoblocker",
		image: `${getImagePath()}media/img/okmodal-png/no_photo.png`,
		headline: _str("First, you need a photo"),
		body: _str("You can’t send messages until you add at least one photo of yourself."),
		ctaText: _str("Add your photo"),
		dismissText: _str("Nah, I’ll talk to myself"),

		onAction(e) {
			e.preventDefault();
			OkModalManager.closeAll();
			OkPhotos().init();
		},
	});
}

export function showMessagePledge(userid, pledge, onAction) {
	new OkModal({
		image: `${getImagePath()}media/img/mobile_app/illustrations/wright_star_buddies.png`,
		headline: pledge.text.title,
		body: pledge.text.content,
		ctaText: pledge.text.confirmation,
		className: "OkModal-message-pledge",
		onAction: () => {
			OkModalManager.closeAll();

			// graduate in the local cache and on the server
			onAction(userid);
		},
	});
}

const ONE_DAY = 24;
const SEVEN_DAYS = 168;

export const ISO_DAYS_TO_STRING = {
	"1": _str("Monday"),
	"2": _str("Tuesday"),
	"3": _str("Wednesday"),
	"4": _str("Thursday"),
	"5": _str("Friday"),
	"6": _str("Saturday"),
	"7": _str("Sunday"),
};

/**
 * Formats a timestamp to be human readable. If the timestamp
 * is <= 24 hours, it should read "09:54 PM".  If the timestamp
 * is > 24 hours but <= 7 days, then it will return the day of the week.
 * If the timestamp is > 7 days, the Month/Date/Year will be returned.
 * @param {number} timestamp - Read receipt timestamp.
 * @returns {string}
 */
export function formatReadReceiptTimeStamp(timestamp) {
	const timestampInMs = timestamp * 1000;
	const differenceInHours = moment().diff((timestampInMs), "hours");

	if (differenceInHours <= ONE_DAY) {
		return moment.unix(timestamp).format("LT");
	} else if (differenceInHours <= SEVEN_DAYS) {
		const iSODay = moment.unix(timestamp).day();
		return ISO_DAYS_TO_STRING[iSODay];
	} else {
		return moment.unix(timestamp).format("MM/DD/YY");
	}
}



// WEBPACK FOOTER //
// ./src/apps/messenger/util/helpers.js